import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Ticket, X, CreditCard, Lock } from "lucide-react";
import { api } from "../lib/api";
import RefundPolicyDialog from "./RefundPolicyDialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";

const eur = (n) => "€ " + Number(n || 0).toFixed(2).replace(".", ",");

export default function CheckoutModal({ open, onOpenChange, plan, cycle, basePrice, planLabel, onConfirm, confirming, ownerBank, cardEnabled = true }) {
  const [promoInput, setPromoInput] = useState("");
  const [policyOpen, setPolicyOpen] = useState(false);
  const [promo, setPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [method, setMethod] = useState("card");

  useEffect(() => {
    if (open) { setPromoInput(""); setPromo(null); setMethod(cardEnabled ? "card" : "bonifico"); }
  }, [open, plan, cycle, cardEnabled]);

  const discounted = promo ? promo.discounted : basePrice;
  const discountAmt = promo ? promo.discount : 0;

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post("/billing/validate-promo", { code, plan, cycle });
      setPromo(data);
      toast.success(`Codice "${data.code}" applicato!`);
    } catch (e) {
      setPromo(null);
      toast.error(e?.response?.data?.detail || "Codice non valido");
    }
    setPromoBusy(false);
  };

  const pay = () => onConfirm(promo?.code || "", method);
  const bank = ownerBank || {};
  const bankOk = !!(bank.iban || "").trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="checkout-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard style={{ width: 18, height: 18 }} className="text-primary" /> Checkout — Piano {planLabel}
          </DialogTitle>
          <DialogDescription>Paga con carta in modo sicuro tramite Stripe oppure con bonifico bancario.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Order summary */}
          <div data-testid="checkout-summary" className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Riepilogo Ordine</p>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Piano {planLabel} <span className="text-slate-400">({cycle === "monthly" ? "mensile" : cycle === "lifetime" ? "una tantum" : "annuale"})</span></span>
              <span data-testid="checkout-base-price" className={`font-mono ${promo ? "line-through text-slate-400" : "font-semibold text-slate-900"}`}>{eur(basePrice)}</span>
            </div>
            {promo && (
              <div data-testid="checkout-discount-row" className="flex items-center justify-between text-sm mt-2 text-emerald-600">
                <span className="inline-flex items-center gap-1.5"><Ticket style={{ width: 14, height: 14 }} /> Codice {promo.code} ({promo.discount_type === "percent" ? `-${promo.discount_value}%` : `-${eur(promo.discount_value)}`})</span>
                <span className="font-mono font-semibold">- {eur(discountAmt)}</span>
              </div>
            )}
            <div className="border-t border-slate-200 mt-3 pt-3 flex items-center justify-between">
              <span className="font-bold text-slate-900">Totale da pagare</span>
              <span data-testid="checkout-final-total" className="font-mono text-xl font-extrabold text-slate-900">{eur(discounted)}</span>
            </div>
          </div>

          {/* Promo */}
          {promo ? (
            <div data-testid="checkout-promo-applied" className="flex items-center justify-between gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5">
              <span className="text-sm font-semibold text-emerald-800 inline-flex items-center gap-1.5"><Ticket style={{ width: 15, height: 15 }} /> Codice "{promo.code}" applicato</span>
              <button data-testid="checkout-promo-remove" onClick={() => { setPromo(null); setPromoInput(""); }} className="text-emerald-700 hover:text-emerald-900"><X style={{ width: 16, height: 16 }} /></button>
            </div>
          ) : (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1.5"><Ticket style={{ width: 13, height: 13 }} /> Codice Promozionale</label>
              <div className="flex gap-2">
                <Input data-testid="checkout-promo-input" value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === "Enter") applyPromo(); }}
                  placeholder="Es. QUOTEFLOW20" className="font-mono uppercase" />
                <Button data-testid="checkout-promo-apply" onClick={applyPromo} disabled={promoBusy || !promoInput.trim()} variant="outline" className="shrink-0 font-semibold">
                  {promoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Applica"}
                </Button>
              </div>
            </div>
          )}

          {/* Payment method */}
          <div className="flex gap-2" data-testid="checkout-method-toggle">
            <button data-testid="checkout-method-card" disabled={!cardEnabled} onClick={() => setMethod("card")}
              className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-semibold transition-colors ${method === "card" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-500"}`}>
              Carta di Credito
            </button>
            <button data-testid="checkout-method-bonifico" disabled={!bankOk} onClick={() => setMethod("bonifico")}
              className={`flex-1 rounded-lg border-2 px-3 py-2 text-sm font-semibold transition-colors ${method === "bonifico" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-500"}`}>
              Bonifico Bancario
            </button>
          </div>

          {method === "bonifico" ? (
            <div data-testid="checkout-bonifico-box" className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-2 text-sm">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Dati per il Bonifico</p>
              <div className="flex justify-between gap-3"><span className="text-slate-500">Intestatario</span><span className="font-semibold text-slate-900 text-right break-words">{bank.holder || "—"}</span></div>
              <div className="flex justify-between gap-3"><span className="text-slate-500">Banca</span><span className="font-semibold text-slate-900 text-right break-words">{bank.bank_name || "—"}</span></div>
              <div className="flex justify-between gap-3"><span className="text-slate-500">IBAN</span><span className="font-mono font-semibold text-slate-900 text-right break-all">{bank.iban || "—"}</span></div>
              {bank.bic && <div className="flex justify-between gap-3"><span className="text-slate-500">BIC/SWIFT</span><span className="font-mono font-semibold text-slate-900 text-right break-all">{bank.bic}</span></div>}
              <p data-testid="checkout-bonifico-note" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
                Il piano sarà attivato <strong>solo ad accredito ricevuto</strong>. Dopo la conferma riceverai la causale con il codice di riferimento da indicare nel bonifico.
              </p>
            </div>
          ) : (
            <div data-testid="checkout-card-box" className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600 flex gap-3">
              <Lock style={{ width: 18, height: 18 }} className="text-emerald-600 shrink-0 mt-0.5" />
              <p>Verrai reindirizzato alla <strong>pagina di pagamento sicura di Stripe</strong> per inserire i dati della carta. {plan === "founder" && cycle === "lifetime" ? "Pagamento unico." : "L'abbonamento si rinnova automaticamente: puoi disdire il rinnovo quando vuoi."}</p>
            </div>
          )}

          <Button data-testid="checkout-pay-button" onClick={pay} disabled={confirming} className="w-full h-11 font-semibold text-base">
            {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock style={{ width: 16, height: 16 }} className="mr-2" />}
            {method === "bonifico" ? `Paga ${eur(discounted)} con bonifico` : `Paga ${eur(discounted)} con carta`}
          </Button>
          <p className="text-[11px] text-slate-400 text-center">Prezzi IVA inclusa · pagamenti con carta gestiti da Stripe</p>
          <button type="button" data-testid="checkout-refund-policy-link" onClick={() => setPolicyOpen(true)}
            className="block mx-auto text-xs font-medium text-primary hover:underline">Garanzia e Condizioni di Rimborso (Totale o Parziale)</button>
        </div>
        <RefundPolicyDialog open={policyOpen} onOpenChange={setPolicyOpen} />
      </DialogContent>
    </Dialog>
  );
}
